import { fetchAPI } from "../lib/api";

const SEARCH_LIMIT = 20;
const SEARCH_DISTANCE = 20;
const SEARCH_DISTANCE_UNIT = "MI";

// Cache for role names
let cachedRoleNames = null;

function asArray(val) {
  if (Array.isArray(val)) return [...val];

  if (typeof val === "string") {
    if (val.includes(",")) return val.split(",").map((str) => str.trim());
    return [val];
  }

  return [];
}

/**
 * Extracts role names from various input formats
 * @param {Array|Object} rolesInput - Can be array of role names, Strapi roles object, or normalized roles array
 * @returns {Array<string>} Array of role name strings
 */
function extractRoleNames(rolesInput) {
  if (!rolesInput) return [];

  // If already an array of strings, return as-is
  if (Array.isArray(rolesInput) && rolesInput.length > 0) {
    if (typeof rolesInput[0] === "string") {
      return rolesInput;
    }
  }

  const rolesArray = rolesInput.data || rolesInput;

  if (!Array.isArray(rolesArray)) return [];

  // v5 format: role fields are directly on the object (flat structure)
  return rolesArray.map((role) => role.roleName).filter(Boolean);
}

async function asCategoryArray(val) {
  if (Array.isArray(val)) return [...val];

  if (typeof val === "string") {
    // Fetch roles if not cached
    let roleNamesToUse = cachedRoleNames;
    if (!roleNamesToUse) {
      try {
        const roles = await fetchAPI(
          "/vacancy-roles",
          {
            fields: ["roleName"],
          },
          { cache: true }
        );
        cachedRoleNames = roles;
        roleNamesToUse = roles;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to fetch role names in mapToSearchObj:", error);
        roleNamesToUse = [];
      }
    }

    // Extract role names from various formats
    const roleNamesArray = extractRoleNames(roleNamesToUse);
    const hasRoles = roleNamesArray.length > 0;

    // Helper to check if a role name is valid
    const isValidRole = (roleName) => roleNamesArray.includes(roleName);

    // Check if the entire string is a valid role
    // This prevents splitting role names that contain commas
    if (hasRoles && isValidRole(val)) {
      return [val];
    }

    // If it contains a comma, try splitting it
    if (val.includes(",")) {
      if (hasRoles) {
        // Try to intelligently split by matching valid roles
        const result = [];
        const parts = val.split(",").map((str) => str.trim());
        let i = 0;

        while (i < parts.length) {
          let matched = false;
          // Try matching progressively longer combinations starting from current position
          for (let j = parts.length; j > i; j -= 1) {
            const combination = parts.slice(i, j).join(", ");
            if (isValidRole(combination)) {
              result.push(combination);
              i = j;
              matched = true;
              break;
            }
          }

          // If no match found, add the current part and move on
          if (!matched) {
            result.push(parts[i]);
            i += 1;
          }
        }

        return result.length > 0 ? result : parts;
      }

      // If no roles available, fall back to original behavior
      return val.split(",").map((str) => str.trim());
    }

    return [val];
  }

  return [];
}

export default async function mapToSearchObj(
  query,
  options,
  accountIdsToFilterBy = []
) {
  const {
    keywords,
    location,
    placeName,
    locationLabel,
    longLat,
    distance,
    category,
    industry,
    hoursType,
    postedDate,
    postedAgeDays,
    county,
    start = 0,
    companyId,
    accountId,
    sortBy,
    order,
    isInternal,
  } = query;

  const { limit, fields, log, excludeContent } = options;

  const searchObj = {
    limit: limit || SEARCH_LIMIT,
    start,
  };

  if (order || sortBy) {
    const sortObj = {};
    if (order) sortObj.order = order.toUpperCase();
    if (sortBy) sortObj.field = sortBy;
    searchObj.sort = [sortObj];
  }

  if (fields && Array.isArray(fields)) searchObj.fields = [...fields];
  if (log !== undefined) searchObj.log = log;
  if (excludeContent) searchObj.excludeContent = true;

  if (location) {
    let coords = [0, 0];

    if (typeof location === "object" && location.coords) {
      coords = [location.coords.lat, location.coords.lng];
      if (typeof location.coords === "string")
        coords = location.coords.split(",");
    } else if (typeof location === "string" && location.includes(",")) {
      coords = location.split(",");
    }

    const [lat, long] = coords;

    // ensure there are coordinates to lookup
    if (lat !== 0 && long !== 0) {
      searchObj.location = {
        distance: Number(distance) || location?.distance || SEARCH_DISTANCE,
        distanceScorePivot: "1km",
        distanceUnit: SEARCH_DISTANCE_UNIT,
        formattedName: locationLabel || placeName || "",
        lat: Number(lat),
        long: Number(long),
      };
    }
  } else if (locationLabel || placeName || longLat) {
    searchObj.location = {
      distance:
        typeof distance !== "undefined"
          ? Number(distance)
          : location?.distance || SEARCH_DISTANCE,
      distanceUnit: SEARCH_DISTANCE_UNIT,
    };

    if (longLat) {
      const [lat, long] = longLat.split(",");

      searchObj.location = {
        ...searchObj.location,
        lat: Number(lat),
        long: Number(long),
      };
    }
  }

  // will overwrite any existing location obj
  if (county) searchObj.location = { county };

  // ensure category is array
  if (category) {
    const categoryArray = await asCategoryArray(category);
    if (categoryArray.length) searchObj.category = categoryArray;
  }

  // ensure industry is array
  if (industry) {
    const industryArray = asArray(industry);
    if (industryArray.length) searchObj.industry = industryArray;
  }

  // ensure accountId is array
  if (accountId) {
    const accIdArray = asArray(accountId);
    if (accIdArray.length) searchObj.accountIds = accIdArray;
  }

  if (accountIdsToFilterBy?.length > 0) {
    if (!searchObj.accountIds) {
      searchObj.accountIds = accountIdsToFilterBy;
    }
  }

  if (keywords) searchObj.keywords = keywords;
  if (hoursType) searchObj.hoursType = hoursType;
  if (postedDate) searchObj.postedDate = postedDate;
  if (postedAgeDays) searchObj.postedAgeDays = postedAgeDays;
  if (companyId) searchObj.companyId = companyId;
  if (isInternal) searchObj.workEnvironment = "INTERNAL";

  return searchObj;
}
